import { SagaIterator } from "redux-saga";
import { eventChannel } from "redux-saga";
import { call, fork, put, SagaGenerator, select, take, takeLatest } from "typed-redux-saga";

import { Activity, ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { ApplicationActions, NEW_INSTANCE_IDENTIFIER } from "@com.mgmtp.a12.client/client-core/lib/core/application";
import { ModelSelectors } from "@com.mgmtp.a12.client/client-core/lib/core/model";
import { LoggerFactory } from "@com.mgmtp.a12.utils/utils-logging";

import { getFVManifestMap, getFVManifests, getLatestFVManifestByKey } from "../../app/slices/fachverfahren/selectors";
import { RuntimeFachverfahrenManifest } from "../../app/slices/fachverfahren/types";
import { getLatestSDManifests, getSDManifests } from "../../app/slices/stammdaten/selectors";
import { RuntimeStammdatenManifest } from "../../app/slices/stammdaten/types";
import { createOpenTaskDocumentPayloadFromProcessInstanceId } from "../../utilities/processInstanceHelpers";
import { CombinedAntragListDescriptor, StammdatenFormDescriptor } from "../../utilities/descriptors";
import { isDocumentNotFoundError } from "../../utilities/restHelper";
import { TEXTBAUSTEINE_TREE } from "../../utilities/variables/documentModelName";
import { DocumentWithPermission, getDocumentWithPermission } from "../../utilities/workflowUtils";

import { openTaskDocument, TASK_DETAIL_ACTIVITY_ID } from "../processOverview/store/actions";
import { openStammdatenAction } from "../stammdaten/actions";

import { applyDeepLink } from "./actions";
import {
	DeepLinkRoute,
	formatDeepLink,
	getApplicationBasePath,
	getDocumentId,
	getDocumentReference,
	isNewerActivity,
	parseDeepLink
} from "./routes";

const logger = LoggerFactory.getLogger("DeepLinking");
const DASHBOARD_DESCRIPTOR: Activity.Descriptor = { dashboard: "dashboard" };
const TEXTBAUSTEINE_DESCRIPTOR: Activity.Descriptor = { model: TEXTBAUSTEINE_TREE, engine: "tree" };

export function* deepLinkingSagas(): SagaIterator<void> {
	yield* takeLatest(applyDeepLink, applyDeepLinkWorker);
	yield* fork(applyBrowserNavigation);
	yield* takeLatest(
		[
			ActivityActions.push,
			ActivityActions.create,
			ActivityActions.cancel,
			ActivityActions.commit.done,
			ActivityActions.setData
		],
		updateDeepLinkWorker
	);
}

function* applyBrowserNavigation(): SagaIterator<void> {
	const popStateChannel = eventChannel<true>(emit => {
		const listener = (): void => emit(true);
		window.addEventListener("popstate", listener);
		return () => window.removeEventListener("popstate", listener);
	});

	while (true) {
		yield* take(popStateChannel);
		yield* put(applyDeepLink());
	}
}

function* applyDeepLinkWorker(): SagaIterator<void> {
	try {
		const route = parseDeepLink(window.location.pathname, getApplicationBasePath());
		if (route && (yield* restoreRoute(route))) {
			return;
		}
	} catch (error) {
		logger.error(error);
	}

	yield* openMainActivity(DASHBOARD_DESCRIPTOR, true);
}

function* restoreRoute(route: DeepLinkRoute): SagaGenerator<boolean> {
	switch (route.type) {
		case "dashboard":
			yield* openMainActivity(DASHBOARD_DESCRIPTOR, true);
			return true;
		case "fachverfahren":
			yield* openMainActivity(CombinedAntragListDescriptor.build({}), false);
			return true;
		case "fachverfahren-overview": {
			const manifest = yield* select(getLatestFVManifestByKey(route.applicationKey));
			if (!manifest) {
				return false;
			}
			const descriptor = yield* getFachverfahrenOverviewDescriptor(manifest);
			if (!descriptor) {
				return false;
			}
			yield* openMainActivity(descriptor, false);
			return true;
		}
		case "fachverfahren-document": {
			const manifests = yield* select(getFVManifests);
			const documentWithPermission = yield* findDocument(
				manifests.filter(manifest => manifest.identification.key === route.applicationKey),
				route.documentId
			);
			if (!documentWithPermission.docSpec) {
				return false;
			}
			const payload = yield* call(createOpenTaskDocumentPayloadFromProcessInstanceId, {
				docSpec: documentWithPermission.docSpec,
				hasPermission: documentWithPermission.hasPermission
			});
			if (!payload) {
				return false;
			}
			yield* put(openTaskDocument(payload));
			return true;
		}
		case "textbausteine":
			yield* openMainActivity(TEXTBAUSTEINE_DESCRIPTOR, false);
			return true;
		case "stammdaten-overview": {
			const manifest = yield* getStammdatenManifest(route.applicationKey);
			if (!manifest) {
				return false;
			}
			yield* openMainActivity({ model: manifest.models.mainDocumentModelId }, false);
			return true;
		}
		case "stammdaten-document": {
			const manifests = yield* select(getSDManifests);
			const manifest = yield* getStammdatenManifest(route.applicationKey);
			if (!manifest) {
				return false;
			}
			const documentWithPermission = yield* findDocument(
				manifests.filter(candidate => candidate.identification.key === route.applicationKey),
				route.documentId
			);
			if (!documentWithPermission?.docSpec) {
				return false;
			}
			yield* put(
				openStammdatenAction({
					docRef: documentWithPermission.docSpec.docRef
				})
			);
			return true;
		}
	}
}

export function* findDocument(
	manifests: RuntimeFachverfahrenManifest[] | RuntimeStammdatenManifest[],
	documentId: string
): SagaGenerator<Partial<DocumentWithPermission>> {
	for (const manifest of [...manifests].sort(
		(first, second) => second.identification.version - first.identification.version
	)) {
		const documentReference = getDocumentReference(documentId, manifest.models.mainDocumentModelId);
		try {
			const documentWithPermission = yield* getDocumentWithPermission(documentReference);
			if (documentWithPermission.docSpec) {
				return documentWithPermission;
			}
		} catch (error) {
			if (!isDocumentNotFoundError(error)) {
				throw error;
			}
		}
	}
	return {};
}

function* openMainActivity(descriptor: Activity.Descriptor, withoutData: boolean): SagaGenerator<void> {
	yield* put(ApplicationActions.startMainActivityRequested({ descriptor, ...(withoutData ? { withoutData } : {}) }));
}

function* getFachverfahrenOverviewDescriptor(
	manifest: RuntimeFachverfahrenManifest
): SagaGenerator<Activity.Descriptor | undefined> {
	const applicationModel = yield* select(ModelSelectors.applicationModel());
	return applicationModel.content.modules
		.map(module => module.menu?.initialActivity?.descriptor)
		.find(descriptor => descriptor?.fachverfahren === manifest.identification.id);
}

function* getStammdatenManifest(applicationKey: string): SagaGenerator<RuntimeStammdatenManifest | undefined> {
	const manifests = yield* select(getLatestSDManifests);
	return manifests.find(manifest => manifest.identification.key === applicationKey);
}

function* updateDeepLinkWorker(): SagaIterator<void> {
	const activities = yield* select(ActivitySelectors.activities());
	const fachverfahrenManifests = yield* select(getFVManifestMap);
	const stammdatenManifests = yield* select(getSDManifests);
	const route = findCurrentRoute(
		Object.values(activities).filter((activity): activity is Activity => activity !== undefined),
		fachverfahrenManifests,
		stammdatenManifests
	);
	if (!route) {
		return;
	}

	const path = formatDeepLink(route, getApplicationBasePath());
	if (window.location.pathname !== path) {
		window.history.pushState(window.history.state, document.title, path);
	}
}

export function findCurrentRoute(
	activities: Activity[],
	fachverfahrenManifests: Record<string, RuntimeFachverfahrenManifest | undefined>,
	stammdatenManifests: RuntimeStammdatenManifest[]
): DeepLinkRoute | undefined {
	let routedActivity: Activity | undefined;
	let route: DeepLinkRoute | undefined;

	for (const activity of activities) {
		const candidate = routeForActivity(activity, fachverfahrenManifests, stammdatenManifests);
		if (
			candidate &&
			(routePriority(candidate) > routePriority(route) ||
				(routePriority(candidate) === routePriority(route) && isNewerActivity(activity, routedActivity)))
		) {
			routedActivity = activity;
			route = candidate;
		}
	}

	return route;
}

function routeForActivity(
	activity: Activity,
	fachverfahrenManifests: Record<string, RuntimeFachverfahrenManifest | undefined>,
	stammdatenManifests: RuntimeStammdatenManifest[]
): DeepLinkRoute | undefined {
	const { descriptor } = activity;

	if (activity.id === TASK_DETAIL_ACTIVITY_ID && descriptor.instance && descriptor.fachverfahren) {
		const manifest = fachverfahrenManifests[descriptor.fachverfahren];
		const applicationKey = manifest?.identification.key;
		return applicationKey && manifest
			? {
					type: "fachverfahren-document",
					applicationKey,
					documentId: getDocumentId(descriptor.instance, manifest.models.mainDocumentModelId)
				}
			: undefined;
	}

	if (StammdatenFormDescriptor.isInstance(descriptor)) {
		if (descriptor.instance === NEW_INSTANCE_IDENTIFIER) {
			return undefined;
		}
		const manifest = stammdatenManifests.find(
			candidate => candidate.models.mainDocumentModelId === descriptor.model
		);
		return manifest
			? {
					type: "stammdaten-document",
					applicationKey: manifest.identification.key,
					documentId: getDocumentId(descriptor.instance, manifest.models.mainDocumentModelId)
				}
			: undefined;
	}

	if (descriptor.dashboard === "dashboard") {
		return { type: "dashboard" };
	}
	if (CombinedAntragListDescriptor.isInstance(descriptor)) {
		return { type: "fachverfahren" };
	}
	if (descriptor.model === TEXTBAUSTEINE_TREE && descriptor.engine === "tree") {
		return { type: "textbausteine" };
	}
	if (descriptor.fachverfahren) {
		const applicationKey = fachverfahrenManifests[descriptor.fachverfahren]?.identification.key;
		return applicationKey ? { type: "fachverfahren-overview", applicationKey } : undefined;
	}

	const stammdatenManifest = stammdatenManifests.find(
		manifest => manifest.models.mainDocumentModelId === descriptor.model
	);
	return stammdatenManifest
		? { type: "stammdaten-overview", applicationKey: stammdatenManifest.identification.key }
		: undefined;
}

function routePriority(route: DeepLinkRoute | undefined): number {
	switch (route?.type) {
		case "fachverfahren-document":
		case "stammdaten-document":
			return 2;
		case "dashboard":
		case "fachverfahren":
		case "fachverfahren-overview":
		case "stammdaten-overview":
		case "textbausteine":
		case undefined:
			return 1;
	}
}
